import React from 'react';
import {Badge, Chip, Icon, WithTooltip} from '@albert-io/atomic';

import {type AuthorizationState, LOCK_AUTH_COPY} from './lockAuthorization';

interface LockAuthorizationIndicatorProps {
  state: AuthorizationState;
  /**
   * The row's explicit value differs from the inherited parent value
   * (classroom ≠ assignment default, or student ≠ classroom). Adds an
   * "Overridden" badge.
   */
  overridden?: boolean;
  /**
   * When the value is inherited rather than explicitly set, the parent level's
   * name — surfaced in a hover tooltip ("Inherited from …").
   */
  inheritedFrom?: string;
  size?: 'small' | 'default';
  className?: string;
}

/**
 * Shared lock-authorization state indicator (Parts 6/7/8). A chip — positive
 * "Authorized" or warning "Not authorized" (warning, not neutral, because
 * not-authorized means students are blocked). Always pairs an icon with the
 * label (never colour-only). Used read-only by teachers and on the assignments
 * list; distinct from the restriction shield (which marks *that* content is
 * restricted, not whether access is granted).
 */
export function LockAuthorizationIndicator({
  state,
  overridden = false,
  inheritedFrom,
  size = 'small',
  className
}: LockAuthorizationIndicatorProps) {
  const authorized = state === 'authorized';

  const chip = (
    <Chip color={authorized ? 'positive' : 'warning'} size={size} className={className}>
      <span className='inline-flex items-center gap-x0-half'>
        <Icon
          icon={authorized ? 'circle-check' : 'circle-minus'}
          iconStyle='regular'
          size='inherit'
        />
        {authorized ? LOCK_AUTH_COPY.authorized : LOCK_AUTH_COPY.notAuthorized}
        {overridden && (
          <Badge status='neutral' size='small'>
            {LOCK_AUTH_COPY.overridden}
          </Badge>
        )}
      </span>
    </Chip>
  );

  if (inheritedFrom) {
    return (
      <WithTooltip content={`Inherited from ${inheritedFrom}`} theme='light' placement='top'>
        {chip}
      </WithTooltip>
    );
  }

  return chip;
}

export default LockAuthorizationIndicator;
